import styled from "styled-components";
import { useMemo, useState } from "react";
import moment from "moment";
import { match } from "ts-pattern";
import { Tag, Classes, Icon, Tab, Tabs } from "@blueprintjs/core";
import { useQuery } from "react-query";
import { getCompanyById } from "ui-io/queries/get-company-by-id";
import { CompanyType } from "ui-io/company-io";
import {
  Box,
  Avatar,
  TitleComponent,
  CenterSpinner,
  ErrorState,
  NoData,
} from "ui-components";

import CompanyOverview from "./overview";
import CompanyTeams from "./teams";
import Financing from "./financing";
import CompanySocial from "./social";
import { useNavigate } from "react-router-dom";
import { formatLocation } from "../../routes";
type Props = {
  companyID?: string;
  isDrawer?: boolean;
  setToggleDrawer?: () => void;
};
const CompanyDetailsView = (props: Props) => {
  const history = useNavigate();
  const { isDrawer = false, setToggleDrawer, companyID } = props;
  const [selectedTabId, setSelectedTabId] = useState("overview");
  const connection = useQuery(
    companyID
      ? getCompanyById({ companyId: companyID })
      : { enabled: !!companyID }
  );
  const [address, setAddress] = useState("");
  const [date, setDate] = useState<Date>();
  const [stageOfFunding, setStageOfFunding] = useState("Unknown");

  const handleDrawerClose = () => {
    if (setToggleDrawer) {
      setToggleDrawer();
    }
    setSelectedTabId("overview");
  };

  const openExpandedView = (companyID: string) => {
    history(
      formatLocation.companyDetails({
        companyID: companyID,
      })
    );
  };

  const handleTabSelect = (source: string) => {
    setSelectedTabId(source);
  };

  const currentCompany = useMemo(() => {
    if (connection.data) {
      const curAddress = connection.data.address
        ? connection.data.address?.[0]
        : "";
      let addressToView = "";
      let foundingDate = connection.data?.incorporation_date ?? "";

      if (curAddress) {
        if (curAddress.country && curAddress.state) {
          addressToView = `${curAddress.state}, ${curAddress.country}`;
        } else if (curAddress.full_address) {
          const segmentAddresses = curAddress.full_address.split(",");
          const state = segmentAddresses.at(-3);
          const country = segmentAddresses.at(-2);
          if (state) {
            addressToView = state.trim();
          }
          if (country) {
            addressToView = addressToView
              ? `${addressToView}, ${country.trim()}`
              : country.trim();
          }
        }
      }

      if (connection.data.incorporation_date) {
        const rawDate = connection.data.incorporation_date;
        foundingDate = rawDate instanceof Date ? rawDate : new Date(rawDate);
      }

      const latestFunding = connection.data?.funding_rounds?.at(-1);
      if (latestFunding && latestFunding.stage) {
        setStageOfFunding(latestFunding.stage);
      }
      setAddress(addressToView);
      setDate(foundingDate);
    }
    return connection.data as CompanyType;
  }, [connection.data]);

  const displayDate = date ? moment(date).format("MMM YYYY") : "";
  return (
    <div className={`p-none ${Classes.DRAWER_BODY} max-w-900px`}>
      <div
        className={Classes.DIALOG_BODY}
        style={
          isDrawer
            ? { margin: 0, maxHeight: "none", padding: "28px" }
            : { margin: 0 }
        }
      >
        <>
          <Header>
            <Box>
              <Box
                flexDir={isDrawer ? "col" : "row"}
                items={isDrawer ? "start" : "center"}
                mb={"small"}
              >
                <Avatar
                  profileUrl={currentCompany?.url?.logo || ""}
                  email={"yantrix@gmail.com"}
                  onlyAvatar={true}
                  displayName={currentCompany?.name || ""}
                  isCircle={true}
                  isCurrentUser={false}
                  isLoading={!connection.isSuccess}
                  size={"60px"}
                />
                <TitleComponent
                  fontSize={"28px"}
                  marginTop={isDrawer ? "5px" : "0px"}
                  marginBottom={"0px"}
                >
                  {currentCompany?.name}
                </TitleComponent>
              </Box>
              <Box flexDir="row">
                <CustomTag>{stageOfFunding}</CustomTag>
                {address && (
                  <Box flexDir="row" items="center" ml={"medium"}>
                    <Icon icon="map-marker" size={12} />
                    <span className="ml-xs">{address}</span>
                  </Box>
                )}
                {displayDate && (
                  <Box flexDir="row" items="center" ml={"medium"}>
                    <Icon icon="calendar" size={12} />
                    <span className="ml-xs">{displayDate}</span>
                  </Box>
                )}
              </Box>
              <IconLinks>
                {currentCompany?.url?.website ? (
                  <a href={currentCompany.url.website} target="_blank">
                    <img src="/icons/web-black.svg" width={35} />
                  </a>
                ) : null}
                {currentCompany?.url?.linkedin ? (
                  <a href={currentCompany.url.linkedin} target="_blank">
                    <img src="/icons/linkedin-black.svg" width={35} />
                  </a>
                ) : null}
                {currentCompany?.url?.twitter ? (
                  <a href={currentCompany.url.twitter} target="_blank">
                    <img src="/icons/twitter-black.svg" width={35} />
                  </a>
                ) : null}
              </IconLinks>
            </Box>
 
            {isDrawer ? (
              <CustomIcon
                icon={"fullscreen"}
                onClick={() =>
                  openExpandedView(currentCompany.companyID || companyID || "")
                }
                size={15}
                right="60px"
                top="23px"
              />
            ) : null}
            {isDrawer ? (
              <CustomIcon icon={"cross"} onClick={handleDrawerClose} size={20} />
            ) : null}
          </Header>

          {match(connection)
            .with({ status: "loading" }, { status: "idle" }, () => (
              <CenterSpinner />
            ))
            .with({ status: "error" }, ({ error }) => (
              <ErrorState error={error} />
            ))
            .with({ status: "success" }, ({ data }: { data: CompanyType }) => {
              if (!data) {
                return (
                  <NoData
                    title="404"
                    icon="warning-sign"
                    description={"No Data found"}
                  />
                );
              }

              return (
                <>
                  <Tabs
                    id={"config-tabs"}
                    large={true}
                    defaultSelectedTabId={"overview"}
                    renderActiveTabPanelOnly={true}
                    selectedTabId={selectedTabId}
                    onChange={handleTabSelect}
                  >
                    <Tab
                      id="overview"
                      title="Overview"
                      panel={<CompanyOverview currentCompany={data} />}
                    />
                    <Tab
                      id="team"
                      title="Team"
                      panel={<CompanyTeams currentCompany={data} />}
                    />
                    <Tab
                      id="financing"
                      title="Financing"
                      panel={<Financing currentCompany={data} />}
                    />
                    <Tab
                      id="socials"
                      title="Socials"
                      panel={<CompanySocial currentCompany={data} />}
                    />
                  </Tabs>
                </>
              );
            })
            .exhaustive()}
        </>
      </div>
    </div>
  );
};

const CustomIcon = styled(Icon)<{ right?: string; top?: string }>`
  position: absolute;
  top: ${(props) => props.top || "20px"};
  right: ${(props) => props.right || "20px"};
  cursor: pointer;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 20px;
`;

const CustomTag = styled(Tag)`
  padding: 3px 10px;
  background-color: #e0f7ec;
  color: #34a853;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
`;

const IconLinks = styled.div`
  margin-top: 15px;
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 1.2em;
  color: #666;

  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: #333;
    }
  }
`;

export default CompanyDetailsView;
